<template>
  <div
    class="player"
    :class="{
      'player--swappable': getIsPlayersSwappingTime,
      'player--selected-for-swapping': isSelectedForSwapping && getIsPlayersSwappingTime,
      'player--roles-are-shown': rolesAreShown(player),
      'player--servant': isServant(player),
      'player--minion': isMinion(player),
      'player--leader': isLeader(player.id),
      'player--me': isMe(player.id),
      'player--chooseable': playersAreChooseable || amAllowedToExecute,
      'player--proposed': isProposed(player.id),
      'player--submitted': isProposed(player.id) && getTeamIsSubmitted,
      'player--acted': hasActed(player.id),
      'player--chosen-for-execution': isChosenForExecution(player.id) && !isExecuted(player.id),
      'player--executed': isExecuted(player.id),
      'player--approved': hasApproved(player.id),
      'player--rejected': hasRejected(player.id),
      'player--executor': isExecutor(player.id),
      'player--disconnected': !isInRoom(player.id),
    }"
    @click="togglePlayer(player.id)"
  >
    <avl-player-avatar
      class="player__player-avatar"
      :id="rolesAreShown(player) ? player.role && player.role.id : null"
      :team="rolesAreShown(player) ? player.role && player.role.team : null"
    />

    <!-- only visible when player is swappable -->
    <img class="player__swap-icon" src="../../assets/img/game/swap-icon.svg" alt="swap icon" />

    <!-- is visible when a player is the leader -->
    <img class="player__crown" src="../../assets/img/game/crown.png" alt="crown" />

    <!-- are visible when a player is proposed for the the team -->
    <img class="player__sword" src="../../assets/img/game/sword.png" alt="sword" />
    <img class="player__shield" src="../../assets/img/game/shield.png" alt="shield" />

    <!-- are visible when player votes are revealed -->
    <avl-team-vote class="player__approval-icon" :type="true" />
    <avl-team-vote class="player__reject-icon" :type="false" />

    <!-- is visible on assassin -->
    <img
      class="player__horn player__horn--left"
      src="../../assets/img/game/horn.png"
      alt="left horn"
    />
    <img
      class="player__horn player__horn--right"
      src="../../assets/img/game/horn.png"
      alt="right horn"
    />

    <!-- is visible when player is chosen for assassination -->
    <img class="player__crosshair" src="../../assets/img/game/crosshair.png" alt="crosshair" />

    <!-- is visible when player is assassinated -->
    <img class="player__x-mark" src="../../assets/img/game/x-mark.png" alt="x mark" />

    <!-- is visible when the player is disconnected from the socket -->
    <img
      class="player__disconnection-mark"
      src="../../assets/img/game/disconnected.svg"
      alt="disconnection mark"
    />

    <div class="player__username-wrapper">
      <span class="player__username">
        {{ player.displayName }}
      </span>
    </div>

    <el-button
      class="player__button-kick"
      v-if="isKickable(player.id)"
      @click.stop="kickPlayer(player.id)"
      type="danger"
      size="mini"
    >
      Kick
    </el-button>

    <el-button
      class="player__button-reveal"
      v-if="!getIsKnowingTime && gameIsStarted && isMe(player.id)"
      @click.stop="$emit('toggleRolesAreRevealed')"
      type="primary"
      size="mini"
    >
      {{ rolesAreRevealed ? 'Hide' : 'Reveal' }}
    </el-button>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import socket from '../../services/socket';

  export default {
    props: {
      player: {
        type: Object,
        required: true,
      },
      isSelectedForSwapping: {
        type: Boolean,
      },
      rolesAreRevealed: {
        type: Boolean,
        required: true,
      },
    },
    computed: {
      ...mapGetters('auth', ['user']),
      ...mapGetters('socket', ['getRoomUsers', 'getIsConnected']),
      ...mapGetters([
        'getIsKnowingTime',
        'getIAmCreator',
        'gameIsCreated',
        'getIsPlayersSwappingTime',
        'gameIsStarted',
        'getTeamProposer',
        'getProposedTeam',
        'getActiveQuestIsSubmitted',
        'getTeamIsSubmitted',
        'getTeamIsApproved',
        'getTeamVotes',
        'getQuestActions',
        'getExecuted',
        'playersAreChooseable',
        'amAllowedToExecute',
        'getExecutor',
      ]),
    },
    methods: {
      rolesAreShown(player) {
        if (!player.role) return false;

        return this.getIsKnowingTime || this.rolesAreRevealed || !this.gameIsStarted;
      },
      isMinion(player) {
        if (!(player && player.role)) return false;

        return player.role.team === 'MINIONS';
      },
      isServant(player) {
        if (!(player && player.role)) return false;

        return player.role.team === 'SERVANTS';
      },
      isKickable(playerId) {
        return this.getIAmCreator && this.gameIsCreated && playerId !== this.user.id;
      },
      kickPlayer(playerId) {
        this.$store.dispatch('game/kickPlayer', {
          gameId: this.$route.params.id,
          playerId: playerId,
        });
      },
      isMe(playerId) {
        return playerId === this.user.id;
      },
      isLeader(playerId) {
        return playerId === this.getTeamProposer.id;
      },
      isProposed(playerId) {
        return !!this.getProposedTeam.find((player) => player.id === playerId);
      },
      hasActed(playerId) {
        // if team is approved
        return this.getTeamIsApproved
          ? // check by success / fails
            this.getQuestActions.find((player) => player.id === playerId)
          : // else - check by approve / rejects
            this.getTeamVotes.find((player) => player.player.id === playerId);
      },
      isExecuted(playerId) {
        return this.isChosenForExecution(playerId) && this.getExecuted.isSubmitted;
      },
      isChosenForExecution(playerId) {
        return this.getExecuted.player.id === playerId;
      },
      togglePlayer(playerId) {
        if (this.getIsPlayersSwappingTime && this.getIAmCreator) {
          this.$emit('toggleSwapSelection', playerId);

          return;
        }

        if (this.playersAreChooseable) {
          socket.emitProposeTeammate(this.$route.params.id, playerId);

          return;
        }

        if (this.amAllowedToExecute) {
          socket.emitProposeExecutionTarget(this.$route.params.id, playerId);
        }
      },
      hasApproved(playerId) {
        const player = this.getTeamVotes.find((player) => player.player.id === playerId);

        if (!player) return false;

        return player.vote === 'APPROVE';
      },
      hasRejected(playerId) {
        const player = this.getTeamVotes.find((player) => player.player.id === playerId);

        if (!player) return false;

        return player.vote === 'REJECT';
      },
      isExecutor(playerId) {
        return this.getExecutor.id === playerId;
      },
      isInRoom(playerId) {
        if (this.isMe(playerId)) return this.getIsConnected;

        return !!this.getRoomUsers.find((p) => p.id === playerId);
      },
      getRoleImage(roleIdRaw) {
        const roleIdKebab = roleIdRaw.toLowerCase().replace('_', '-');

        const imageUrl = require(`../../assets/img/characters/${roleIdKebab}.png`);

        return `url(${imageUrl})`;
      },
    },
  };
</script>

<style lang="scss">
  .player {
    position: relative;
    vertical-align: middle;
    user-select: none;
    width: 112px;
    height: 112px;
    text-align: center;
    font-size: 14px;
    transition: all 200ms;

    &--minion {
    }

    &--servant {
    }

    &--leader {
    }

    &--me {
    }

    &--chooseable {
      cursor: pointer;
    }

    &--proposed {
    }

    &--submitted {
    }

    &--submitted &__shield {
      opacity: 1;
    }

    &--acted .player-avatar__background {
      opacity: 0.75;
    }

    &__approval-icon,
    &__reject-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      //
      transition: transform 400ms, opacity 400ms;
      transform: translate(-50%, 100%);
      opacity: 0;
      //
      display: block;
      width: 28px;
      border-radius: 50%;
      border: 1px solid;
    }

    &--approved &__approval-icon,
    &--rejected &__reject-icon {
      transform: translate(-50%, 80%);
      opacity: 1;
    }

    &--chosen-for-execution {
    }

    &--executed {
    }

    &--executor {
    }

    &__swap-icon,
    &__crown,
    &__sword,
    &__shield,
    &__horn,
    &__crosshair,
    &__x-mark,
    &__disconnection-mark {
      opacity: 0;
      pointer-events: none;
      //
      position: absolute;
      //
      transition: opacity 200ms;
    }

    &__swap-icon {
      width: 72px;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    &--swappable &__swap-icon {
      opacity: 0.2;
    }

    &--selected-for-swapping &__swap-icon {
      opacity: 1;
    }

    &__crown {
      width: 48px;
      //
      top: 6px;
      left: 25px;
      transform: translate(-50%, -50%) rotate(-12deg);
    }

    &--leader &__crown {
      opacity: 1;
    }

    &__sword {
      width: 80px;
      //
      top: 52px;
      left: 0;
      transform: translate(-37%, -39%) rotate(40deg);
    }

    &--proposed &__sword {
      opacity: 1;
    }

    &__shield {
      width: 48px;
      //
      top: 60px;
      right: 8px;
      transform: translate(50%, -50%) rotate(17deg);
    }

    &__horn {
      opacity: 0;
      width: 32px;
    }

    &__horn--left {
      top: 8px;
      left: 0;
      transform: translate(50%, -50%) rotate(-8deg);
    }

    &__horn--right {
      top: 8px;
      right: 0;
      transform: translate(-50%, -50%) scaleX(-1) rotate(-8deg);
    }

    &--executor &__horn {
      opacity: 1;
    }

    &__crosshair {
      width: 100px;
      top: 50%;
      left: 50%;
      transform: translate(-70%, -85%) rotate(17deg);
    }

    &--chosen-for-execution &__crosshair {
      opacity: 1;
    }

    &__x-mark {
      width: 72px;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -35%);
    }

    &--executed &__x-mark {
      opacity: 1;
    }

    &__disconnection-mark {
      position: absolute;
      bottom: 0;
      right: 0;
      transform: translate(-50%, -50%) scale(1.5);
    }

    &--disconnected &__disconnection-mark {
      opacity: 1;
    }

    &__username-wrapper {
      width: 120%;
      height: 30px;
      background: url(../../assets/img/game/name-frame.png) no-repeat center;
      background-size: contain;
      margin-bottom: -8px;
      //
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translate(-50%, 50%);
    }

    &__username {
      display: block;
      color: #ffffff;
      font-size: 16px;
      //
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      //
      width: 92.5%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__tooltip {
      width: 120px;
      text-align: center;
    }

    &__button-kick,
    &__button-reveal {
      position: absolute;
      bottom: 0;
      left: 50%;
      //
      display: block;
      min-width: 60px;

      &:hover,
      &:disabled {
        opacity: 0.8;
      }
    }

    &__button-kick {
      transform: translate(-50%, 200%);
    }

    &__button-reveal {
      transform: translate(-50%, 200%);
    }
  }
</style>
